import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { Link } from "react-router-dom";
import { Plus, ChevronRight, Star } from "lucide-react";

const DESIGNATION_LABEL = { FOUNDING_HOME_CLUB: "Founding Home Club", LV_PARTNER: "LV Partner", LV_GLOBAL_PARTNER: "LV Global Partner" };

export default function PartnerList() {
  const { data: partners = [], isLoading } = useQuery({ queryKey: ["admin-partners"], queryFn: () => api.get("/admin/partners") });

  return (
    <div data-testid="admin-partner-list">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="font-serif text-2xl sm:text-3xl text-cream tracking-wide">LV Partners</h1>
          <p className="text-sm text-lv-muted tracking-wide mt-1">{partners.length} partners</p>
        </div>
        <Link to="/admin/partners/new" className="lv-btn-primary inline-flex items-center gap-2 min-h-[44px]" data-testid="admin-create-partner-btn">
          <Plus size={16} /> New Partner
        </Link>
      </div>

      {isLoading ? (
        <div className="text-center py-12"><div className="w-10 h-0.5 bg-bronze mx-auto animate-pulse" /></div>
      ) : partners.length === 0 ? (
        <div className="text-center py-16 text-lv-muted text-sm" data-testid="admin-no-partners">No partners yet</div>
      ) : (
        <div className="space-y-3">
          {partners.map((p) => (
            <Link key={p.id} to={`/admin/partners/${p.id}`} className="block group" data-testid={`admin-partner-${p.id}`}>
              <div className="bg-walnut border border-lv-border p-4 sm:p-5 flex items-center gap-4 hover:border-bronze/40 transition-colors">
                {p.cover_image_url ? (
                  <div className="w-16 h-16 rounded-sm overflow-hidden shrink-0 border border-lv-border">
                    <img src={p.cover_image_url} alt="" className="w-full h-full object-cover" />
                  </div>
                ) : (
                  <div className="w-16 h-16 rounded-sm bg-walnut-deep border border-lv-border flex items-center justify-center shrink-0">
                    <span className="text-xs text-lv-muted">{p.name?.charAt(0)}</span>
                  </div>
                )}
                <div className="min-w-0 flex-1">
                  <h3 className="text-base text-cream font-medium tracking-wide truncate">{p.name}</h3>
                  <div className="flex items-center gap-2 mt-1 flex-wrap">
                    <span className={`text-[10px] tracking-[0.2em] uppercase ${p.designation === "FOUNDING_HOME_CLUB" ? "text-bronze" : "text-lv-muted"}`}>
                      {p.designation === "FOUNDING_HOME_CLUB" && <Star size={10} className="inline mr-1" />}
                      {DESIGNATION_LABEL[p.designation] || p.designation}
                    </span>
                    {p.categories?.map((c) => <span key={c} className="text-[9px] text-lv-muted/60 uppercase tracking-wider">{c}</span>)}
                  </div>
                  <div className="flex items-center gap-3 mt-1">
                    <span className="text-xs text-lv-muted">{p.privilege_count || 0} privilege{p.privilege_count !== 1 ? "s" : ""}</span>
                    <span className={`text-[10px] uppercase tracking-wider ${p.status === "ACTIVE" ? "text-lv-active" : "text-lv-inactive"}`}>{p.status}</span>
                  </div>
                </div>
                <ChevronRight size={18} className="text-lv-muted group-hover:text-bronze transition-colors shrink-0" />
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
